import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { Badge, Modal, toast, PropertyPreviewModal, ViewingResultModal, ApplicationSubmissionModal } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { buildApplyUrl } from '@/lib/apply-url';
import styles from './CRM.module.css';

interface Customer {
  id: string;
  name: string;
  phone: string;
  email: string;
  source: string;
  status: string;
  assignee: string;
  assignee_id?: string;
  preferences: Record<string, unknown>;
  notes: string;
  created_at: string;
  property_interest?: string;
  interested_property_id?: string;
  line_id?: string;
}

interface Property {
  id: string;
  name: string;
  address: string;
  layout: string;
  area: number;
  rent: number;
}

interface Activity {
  id: string;
  activity_type: string;
  title: string;
  description: string;
  created_at: string;
}

interface Proposal {
  id: string;
  customer_id: string;
  property_id: string;
  property_name?: string;
  address?: string;
  layout?: string;
  rent?: number;
  status: string;
  created_at: string;
}

const proposalStatusLabel: Record<string, string> = {
  proposed: '提案中', interested: '興味あり', viewing: '内見予定', rejected: '見送り',
};
const proposalStatusVariant: Record<string, string> = {
  proposed: 'blue', interested: 'green', viewing: 'amber', rejected: 'gray',
};

interface TenantUser {
  id: string;
  name: string;
}

const statusLabel: Record<string, string> = {
  new: '新規', interested: '連絡中',
  viewing: '内見予定', viewing_done: '内見済',
  applied: '申込中', negotiating: '交渉中', contracted: '成約', lost: '失注',
  // 後方互換: 既存 contacted データは migrate-065 で interested に統合済
  contacted: '連絡中',
};

const statusVariant: Record<string, 'blue' | 'green' | 'amber' | 'purple' | 'red' | 'gray'> = {
  new: 'blue', interested: 'purple',
  viewing: 'amber', viewing_done: 'amber',
  applied: 'green', negotiating: 'amber', contracted: 'green', lost: 'gray',
  // 後方互換
  contacted: 'purple',
};

const sourceLabel: Record<string, string> = {
  suumo: 'SUUMO', homes: "HOME'S", athome: 'athome',
  website: '自社サイト', walk_in: '来店', referral: '紹介', line: 'LINE', other: 'その他',
};

const activityIcon: Record<string, string> = {
  inquiry: '📨', call: '📞', email: '📧', viewing: '🏠', note: '📝',
  application: '📝', contract: '✅', line: '💬',
};

const customerTypes = [
  { value: 'tenant_candidate', label: '借主候補（賃貸）' },
  { value: 'buyer_candidate', label: '買主候補（売買）' },
  { value: 'owner', label: 'オーナー（貸主）' },
  { value: 'seller', label: '売主' },
  { value: 'other', label: 'その他' },
];

const layoutOptions = ['指定なし', '1R', '1K', '1DK', '1LDK', '2K', '2DK', '2LDK', '3K', '3DK', '3LDK', '4LDK', '5LDK+'];
const petOptions = ['不問', '可（小型犬）', '可（猫）', '可（小型犬・猫）', '不可'];
const moveInOptions = (() => {
  const opts = ['未定', '即入居'];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    opts.push(`${d.getFullYear()}年${String(d.getMonth() + 1).padStart(2, '0')}月`);
  }
  return opts;
})();

type Tab = 'overview' | 'history' | 'proposals' | 'viewings' | 'deals';

interface Viewing {
  id: string;
  customer_id: string;
  property_id: string | null;
  property_name: string | null;
  property_address: string | null;
  scheduled_at: string;
  duration_minutes: number;
  assignee_id: string | null;
  meeting_place: string | null;
  status: 'scheduled' | 'done' | 'cancelled' | 'no_show';
  result: 'interested' | 'applied' | 'rejected' | null;
  note: string | null;
  reminded_at: string | null;
  created_at: string;
}

const viewingStatusLabel: Record<string, string> = {
  scheduled: '予定', done: '完了', cancelled: 'キャンセル', no_show: '不来訪',
};
const viewingStatusVariant: Record<string, 'blue' | 'green' | 'gray' | 'red'> = {
  scheduled: 'blue', done: 'green', cancelled: 'gray', no_show: 'red',
};
const viewingResultLabel: Record<string, string> = {
  interested: '興味あり', applied: '申込', rejected: '見送り',
};

interface Deal {
  id: string;
  deal_type: 'lease' | 'sale' | 'management';
  title: string;
  status: string;
  property_id?: string;
  property_name?: string;
  customer_id?: string;
  customer_name?: string;
  created_at: string;
  closed_at?: string | null;
}

interface ApplicationForm {
  id: string;
  token: string;
  status: string;
  property_id: string | null;
  property_name: string | null;
  property_address: string | null;
  latest_submission_id: string | null;
  latest_submission_status: string | null;
  created_at: string;
}

const submissionStatusLabel: Record<string, string> = {
  received: '受付', reviewing: '審査中', approved: '承認', rejected: '却下',
};
const submissionStatusVariant: Record<string, 'blue' | 'green' | 'amber' | 'red' | 'gray'> = {
  received: 'amber', reviewing: 'blue', approved: 'green', rejected: 'red',
};

const dealTypeLabel: Record<string, string> = { lease: '賃貸取引', sale: '売買取引', management: '管理委託' };
const dealTypeIcon: Record<string, string> = { lease: '🏠', sale: '🏢', management: '🔑' };
const dealStatusLabel: Record<string, string> = { in_progress: '進行中', completed: '完了', cancelled: '中止' };
const dealStatusVariant: Record<string, 'blue' | 'green' | 'amber' | 'purple' | 'red' | 'gray'> = {
  in_progress: 'blue', completed: 'green', cancelled: 'gray',
};

export function CRM() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user: authUser } = useAuthStore();
  const isStaffOnly = authUser?.role === 'tenant_staff';
  const [scope, setScope] = useState<'all' | 'mine' | 'unassigned'>(isStaffOnly ? 'mine' : 'all');
  const { data: customers, loading, refetch } = useApi<Customer[]>(`/tenant/customers?scope=${scope}`);
  const { data: properties } = useApi<Property[]>('/tenant/properties');
  const { data: users } = useApi<TenantUser[]>('/tenant/users');
  const [selected, setSelected] = useState<Customer | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddHistory, setShowAddHistory] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [previewTarget, setPreviewTarget] = useState<{ id: string; name: string } | null>(null);
  const [selectedProposalIds, setSelectedProposalIds] = useState<Set<string>>(new Set());
  const [showBundlePreview, setShowBundlePreview] = useState(false);
  // 内見履歴
  const [viewings, setViewings] = useState<Viewing[]>([]);
  const [showViewingForm, setShowViewingForm] = useState(false);
  const [viewingForm, setViewingForm] = useState({ propertyId: '', date: '', time: '', meetingPlace: '', note: '', assigneeId: '' });
  const [editingViewingId, setEditingViewingId] = useState<string | null>(null);
  // 結果登録モーダル (viewingId を保持)
  const [resultTargetId, setResultTargetId] = useState<string | null>(null);
  const [customerDeals, setCustomerDeals] = useState<Deal[]>([]);
  const [applicationForms, setApplicationForms] = useState<ApplicationForm[]>([]);
  const [submissionModalId, setSubmissionModalId] = useState<string | null>(null);

  // Create form - mock準拠: 姓名分離 + 顧客種別 + 希望条件 + 流入・担当
  const [createForm, setCreateForm] = useState({
    lastName: '', firstName: '',
    phone: '', email: '', line_id: '',
    customer_type: 'tenant_candidate',
    area: '', layout: '指定なし', max_rent: '', move_in: '未定', pet: '不問',
    notes: '',
    source: 'suumo', assignee: '',
    privacy_consent: true,
    interested_property_id: '',
  });

  const [editForm, setEditForm] = useState({
    name: '', phone: '', email: '', status: '', assignee: '', assignee_id: '', property_interest: '', notes: '', interested_property_id: '',
    pref_area: '', pref_layout: '', pref_max_rent: '', pref_move_in: '', pref_pet: '', pref_customer_type: '', source: '',
  });
  const [historyForm, setHistoryForm] = useState({
    activity_type: 'note', title: '', description: '',
  });

  useEffect(() => {
    const id = searchParams.get('id');
    if (id && customers) {
      const customer = customers.find(c => c.id === id);
      if (customer) { setSelected(customer); setActiveTab('overview'); }
    }
  }, [searchParams, customers]);

  useEffect(() => {
    if (selected && activeTab === 'history') {
      api<Activity[]>(`/tenant/customers/${selected.id}/activities`).then(setActivities).catch(() => setActivities([]));
    }
    if (selected && activeTab === 'proposals') {
      api<Proposal[]>(`/tenant/customers/${selected.id}/proposals`).then(setProposals).catch(() => setProposals([]));
    }
    if (selected && activeTab === 'viewings') {
      api<Viewing[]>(`/tenant/customers/${selected.id}/viewings`).then(setViewings).catch(() => setViewings([]));
    }
    if (selected && activeTab === 'deals') {
      api<Deal[]>(`/tenant/deals?customer_id=${selected.id}`).then(setCustomerDeals).catch(() => setCustomerDeals([]));
    }
    // applied 顧客を選択中は overview タブで申込フォーム情報を表示する
    if (selected && activeTab === 'overview' && selected.status === 'applied') {
      api<ApplicationForm[]>(`/tenant/customers/${selected.id}/application-forms`).then(setApplicationForms).catch(() => setApplicationForms([]));
    } else if (selected && activeTab === 'overview') {
      setApplicationForms([]);
    }
  }, [selected, activeTab]);

  // 担当者ドロップダウン: users テーブル（UUID）ベース、自分自身は除外（スコープセグメントと重複回避）
  const assigneeOptions = useMemo(() => {
    if (!users) return [];
    return users
      .filter(u => u.id !== authUser?.id)
      .map(u => ({ id: u.id, name: u.name }));
  }, [users, authUser?.id]);

  const filtered = useMemo(() => {
    if (!customers) return [];
    return customers.filter((c) => {
      const matchSearch = !search || c.name.includes(search) || c.phone.includes(search) || (c.email && c.email.includes(search));
      const matchStatus = !statusFilter || c.status === statusFilter;
      const matchAssignee = !assigneeFilter || c.assignee_id === assigneeFilter;
      return matchSearch && matchStatus && matchAssignee;
    });
  }, [customers, search, statusFilter, assigneeFilter]);

  const handleSelect = (c: Customer) => { setSelected(c); setActiveTab('overview'); setSelectedProposalIds(new Set()); };

  const pref = (key: string): string => {
    if (!selected?.preferences) return '—';
    const v = selected.preferences[key];
    if (v === undefined || v === null || v === '') return '—';
    return String(v);
  };

  const getSuggestedProperties = (): Property[] => {
    if (!properties || !selected) return [];
    const prefs = selected.preferences || {};
    const area = String(prefs.area || '');
    const maxRent = prefs.max_rent ? parseInt(String(prefs.max_rent).replace(/[^0-9]/g, ''), 10) : 0;
    const layout = String(prefs.layout || '');
    return properties.filter(p => {
      if (area && !p.address.includes(area.split('・')[0])) return false;
      if (maxRent > 0 && p.rent > maxRent) return false;
      if (layout && layout !== '指定なし' && !layout.includes(p.layout)) return false;
      return true;
    }).slice(0, 6);
  };

  const handleCreateCustomer = async () => {
    const name = `${createForm.lastName} ${createForm.firstName}`.trim();
    if (!name) { toast('姓は必須です', 'error'); return; }
    // createForm.assignee には user.id が入る（selector で value={u.id}）。名前キャッシュも併せて保存。
    const assigneeUser = (users ?? []).find(u => u.id === createForm.assignee);
    try {
      await api('/tenant/customers', {
        method: 'POST',
        body: JSON.stringify({
          name,
          phone: createForm.phone,
          email: createForm.email,
          line_id: createForm.line_id || undefined,
          source: createForm.source,
          status: 'new',
          assignee: assigneeUser?.name || '',
          assignee_id: createForm.assignee || null,
          property_interest: createForm.area ? `${createForm.area} ${createForm.layout !== '指定なし' ? createForm.layout : ''} ${createForm.max_rent ? `¥${Number(createForm.max_rent).toLocaleString()}以内` : ''}`.trim() : '',
          preferences: {
            area: createForm.area,
            layout: createForm.layout !== '指定なし' ? createForm.layout : '',
            max_rent: createForm.max_rent ? `¥${Number(createForm.max_rent).toLocaleString()}/月` : '',
            move_in: createForm.move_in !== '未定' ? createForm.move_in : '',
            pet: createForm.pet !== '不問' ? createForm.pet : '',
            customer_type: customerTypes.find(ct => ct.value === createForm.customer_type)?.label ?? '',
          },
          notes: createForm.notes,
          privacy_consent: createForm.privacy_consent,
          interested_property_id: createForm.interested_property_id || undefined,
        }),
      });
      toast('顧客を登録しました', 'success');
      setShowCreate(false);
      setCreateForm({ lastName: '', firstName: '', phone: '', email: '', line_id: '', customer_type: 'tenant_candidate', area: '', layout: '指定なし', max_rent: '', move_in: '未定', pet: '不問', notes: '', source: 'suumo', assignee: '', privacy_consent: true, interested_property_id: '' });
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '登録に失敗しました', 'error');
    }
  };

  const handleEditCustomer = async () => {
    if (!selected) return;
    // 退行ガード: contracted からの変更はブロック
    if (selected.status === 'contracted' && editForm.status !== 'contracted') {
      toast('成約済み顧客のステータスは変更できません', 'error');
      return;
    }
    // 申込中への遷移時はフォーム発行可否を確認
    let issueApplicationForm = false;
    const movingToApplied = editForm.status === 'applied' && selected.status !== 'applied';
    if (movingToApplied) {
      if (!editForm.interested_property_id) {
        toast('関心物件が未選択のため申込フォームを発行できません。関心物件を選択してください。', 'error');
        return;
      }
      issueApplicationForm = confirm('申込フォーム URL を発行して顧客に送付できるようにしますか?\n「OK」で発行、「キャンセル」でステータス変更のみ');
    }
    // 退行警告: applied から viewing_done/viewing/interested 等への退行
    const BACK_STATES = new Set(['new', 'interested', 'contacted', 'viewing', 'viewing_done']);
    if (selected.status === 'applied' && BACK_STATES.has(editForm.status)) {
      if (!confirm('「申込中」から前段階のステータスへ戻します。申込フォームは残りますがよろしいですか?')) return;
    }
    try {
      const { pref_area, pref_layout, pref_max_rent, pref_move_in, pref_pet, pref_customer_type, source, ...rest } = editForm;
      const payload: Record<string, unknown> = {
        ...rest,
        source,
        preferences: { area: pref_area, layout: pref_layout, max_rent: pref_max_rent, move_in: pref_move_in, pet: pref_pet, customer_type: pref_customer_type },
      };
      if (issueApplicationForm) payload.issue_application_form = true;
      const updated = await api<Customer & { application_form_token?: string | null }>(`/tenant/customers/${selected.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      toast('顧客情報を更新しました', 'success');
      if (updated.application_form_token) {
        const url = buildApplyUrl(updated.application_form_token, { slug: authUser?.tenantSlug });
        navigator.clipboard?.writeText(url).then(
          () => toast(`申込フォーム URL をコピーしました: ${url}`, 'success'),
          () => toast(`申込フォーム URL: ${url}`, 'info'),
        );
      }
      setShowEdit(false);
      setSelected(updated);
      refetch();
      if (updated.status === 'applied') {
        api<ApplicationForm[]>(`/tenant/customers/${selected.id}/application-forms`).then(setApplicationForms).catch(() => {});
      }
    } catch { toast('更新に失敗しました', 'error'); }
  };

  const handleCopyApplyUrl = (token: string) => {
    const url = buildApplyUrl(token, { slug: authUser?.tenantSlug });
    navigator.clipboard?.writeText(url).then(
      () => toast('URL をコピーしました', 'success'),
      () => toast('コピーに失敗しました', 'error'),
    );
  };

  const openEditModal = () => {
    if (!selected) return;
    const prefs = selected.preferences || {};
    setEditForm({ name: selected.name, phone: selected.phone, email: selected.email, status: selected.status, assignee: selected.assignee || '', assignee_id: selected.assignee_id || '', property_interest: selected.property_interest || '', notes: selected.notes || '', interested_property_id: selected.interested_property_id || '', pref_area: String(prefs.area ?? ''), pref_layout: String(prefs.layout ?? ''), pref_max_rent: String(prefs.max_rent ?? ''), pref_move_in: String(prefs.move_in ?? ''), pref_pet: String(prefs.pet ?? ''), pref_customer_type: String(prefs.customer_type ?? ''), source: selected.source || '' });
    setShowEdit(true);
  };

  const handleAddHistory = async () => {
    if (!selected) return;
    try {
      await api(`/tenant/customers/${selected.id}/activities`, { method: 'POST', body: JSON.stringify(historyForm) });
      toast('対応履歴を追加しました', 'success');
      setShowAddHistory(false);
      setHistoryForm({ activity_type: 'note', title: '', description: '' });
      const data = await api<Activity[]>(`/tenant/customers/${selected.id}/activities`);
      setActivities(data);
    } catch { toast('追加に失敗しました', 'error'); }
  };

  const handlePropose = async (propertyId: string) => {
    if (!selected) return;
    try {
      await api(`/tenant/customers/${selected.id}/proposals`, { method: 'POST', body: JSON.stringify({ property_id: propertyId }) });
      toast('物件を提案しました', 'success');
      api<Proposal[]>(`/tenant/customers/${selected.id}/proposals`).then(setProposals).catch(() => {});
    } catch { toast('提案に失敗しました', 'error'); }
  };

  const handleProposalStatus = async (proposalId: string, status: string) => {
    if (!selected) return;
    try {
      await api(`/tenant/customers/${selected.id}/proposals/${proposalId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      toast('ステータスを更新しました', 'success');
      api<Proposal[]>(`/tenant/customers/${selected.id}/proposals`).then(setProposals).catch(() => {});
    } catch { toast('更新に失敗しました', 'error'); }
  };

  // 提案物件の選択 (複数紹介書出力用)
  const toggleProposalSelection = (propertyId: string) => {
    setSelectedProposalIds(prev => {
      const next = new Set(prev);
      if (next.has(propertyId)) next.delete(propertyId);
      else next.add(propertyId);
      return next;
    });
  };
  const selectAllProposals = () => {
    setSelectedProposalIds(new Set(proposals.filter(pr => pr.status !== 'rejected').map(pr => pr.property_id)));
  };
  const clearProposalSelection = () => setSelectedProposalIds(new Set());

  return (
    <div className={styles.page}>
      {/* Left panel */}
      <div className={styles.listPanel}>
        <div className={styles.listHeader}>
          <button className={styles.createBtn} onClick={() => setShowCreate(true)}>＋ 顧客登録</button>
          {/* スコープセグメント: staff は固定、owner/manager は切替可能 */}
          <div style={{ display: 'flex', gap: 0, margin: '8px 0', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {(['all', 'mine', 'unassigned'] as const).map(s => {
              const label = s === 'all' ? '全社' : s === 'mine' ? '自分の担当' : '未割当';
              const disabled = isStaffOnly && s !== 'mine';
              return (
                <button key={s}
                  onClick={() => !disabled && setScope(s)}
                  disabled={disabled}
                  style={{
                    flex: 1, padding: '6px 4px', fontSize: 11, fontWeight: 600, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
                    background: scope === s ? 'var(--primary, #0ea5e9)' : 'var(--surface2)',
                    color: scope === s ? '#fff' : disabled ? 'var(--ink4)' : 'var(--ink2)',
                    opacity: disabled ? 0.5 : 1,
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
          <div className={styles.searchRow}>
            <span className={styles.searchIcon}>🔍</span>
            <input className={styles.searchInput} type="text" placeholder="顧客名・電話番号" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className={styles.filterRow}>
            <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">全ステータス</option>
              <option value="new">新規</option><option value="interested">連絡中</option>
              <option value="viewing">内見予定</option><option value="viewing_done">内見済</option>
              <option value="applied">申込中</option><option value="contracted">成約</option><option value="lost">失注</option>
            </select>
            {!isStaffOnly && assigneeOptions.length > 0 && (
              <select className={styles.filterSelect} value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
                <option value="">全担当者</option>
                {assigneeOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className={styles.customerList}>
          {loading ? <div style={{ padding: 20, color: 'var(--ink3)' }}>読込中...</div>
          : filtered.length === 0 ? <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13 }}>顧客が見つかりません</div>
          : filtered.map((c) => (
            <div key={c.id} className={`${styles.customerCard} ${selected?.id === c.id ? styles.selected : ''}`} onClick={() => handleSelect(c)}>
              <div className={styles.avatar}>{c.name.charAt(0)}</div>
              <div className={styles.cardBody}>
                <div className={styles.cardName}>{c.name}</div>
                <div className={styles.cardSub}>{c.property_interest || ''}</div>
              </div>
              <Badge variant={statusVariant[c.status] ?? 'gray'}>{statusLabel[c.status] ?? c.status}</Badge>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className={styles.detailPanel}>
        {!selected ? <div className={styles.emptyDetail}>顧客を選択してください</div> : (
          <>
            <div className={styles.detailHeader}>
              <div className={styles.detailAvatar}>{selected.name.charAt(0)}</div>
              <div className={styles.detailMeta}>
                <div className={styles.detailName}>{selected.name}</div>
                <div className={styles.detailContact}>
                  {selected.phone && <span>📱 {selected.phone}</span>}
                  {selected.email && <span>  |  📧 {selected.email}</span>}
                  {selected.assignee && <span>  |  担当: {selected.assignee}</span>}
                </div>
              </div>
              <div className={styles.detailActions}>
                <button className={styles.actionBtn} onClick={() => selected.phone && window.open(`https://line.me/R/msg/text/?${encodeURIComponent(`${selected.name}様`)}`)}>💬 LINE</button>
                <button className={styles.actionBtn} onClick={() => selected.email && (window.location.href = `mailto:${selected.email}`)}>📧 メール</button>
                <button className={styles.actionBtnOutline} onClick={openEditModal}>✏️ 編集</button>
              </div>
            </div>

            <div className={styles.tabBar}>
              {([['overview', '概要'], ['history', '対応履歴'], ['proposals', '提案物件'], ['viewings', '内見'], ['deals', '取引']] as [Tab, string][]).map(([key, label]) => (
                <button key={key} className={`${styles.tab} ${activeTab === key ? styles.active : ''}`} onClick={() => setActiveTab(key)}>{label}</button>
              ))}
            </div>

            <div className={styles.tabContent}>
              {activeTab === 'overview' && (
                <>
                  <div className={styles.overviewGrid}>
                    {[['希望エリア', pref('area')], ['希望間取り', pref('layout')], ['賃料上限', pref('max_rent')], ['入居希望時期', pref('move_in')], ['問い合わせ元', sourceLabel[selected.source] ?? selected.source], ['ペット', pref('pet')], ['顧客種別', customerTypes.find(ct => ct.value === pref('customer_type'))?.label ?? pref('customer_type')]].map(([label, value]) => (
                      <div key={label as string} className={styles.overviewItem}>
                        <div className={styles.overviewLabel}>{label}</div>
                        <div className={styles.overviewValue}>{value}</div>
                      </div>
                    ))}
                    <div className={styles.overviewItem}>
                      <div className={styles.overviewLabel}>ステータス</div>
                      <div className={styles.overviewValue}><Badge variant={statusVariant[selected.status] ?? 'gray'}>{statusLabel[selected.status] ?? selected.status}</Badge></div>
                    </div>
                  </div>
                  {selected.status === 'applied' && applicationForms.length > 0 && (
                    <div className={styles.notesSection}>
                      <div className={styles.notesTitle}>📝 申込フォーム</div>
                      {applicationForms.filter(f => f.status === 'open').map(f => (
                        <div key={f.id} style={{ padding: '8px 10px', background: 'var(--surface2)', borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <div style={{ fontWeight: 600 }}>{f.property_name || '物件未指定'}</div>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              {f.latest_submission_status && (
                                <Badge variant={submissionStatusVariant[f.latest_submission_status] ?? 'gray'}>
                                  {submissionStatusLabel[f.latest_submission_status] ?? f.latest_submission_status}
                                </Badge>
                              )}
                              <button type="button" className={styles.actionBtnOutline} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => handleCopyApplyUrl(f.token)}>URL コピー</button>
                              {f.latest_submission_id && (
                                <button type="button" className={styles.actionBtnOutline} style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => setSubmissionModalId(f.latest_submission_id)}>申込書を見る</button>
                              )}
                            </div>
                          </div>
                          <div style={{ color: 'var(--ink3)', fontSize: 11, wordBreak: 'break-all' }}>{buildApplyUrl(f.token, { slug: authUser?.tenantSlug })}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.notesSection}>
                    <div className={styles.notesTitle}>備考・要望</div>
                    <div className={styles.notesBody}>{selected.notes || pref('notes') || '特になし'}</div>
                  </div>
                </>
              )}
              {activeTab === 'history' && (
                <div className={styles.historyTab}>
                  <div className={styles.historyHeader}>
                    <span className={styles.historyTitle}>📅 対応履歴</span>
                    <button className={styles.historyAddBtn} onClick={() => setShowAddHistory(true)}>＋ 対応履歴追加</button>
                  </div>
                  <div className={styles.timeline}>
                    {activities.length > 0 ? activities.map((entry) => (
                      <div key={entry.id} className={styles.timelineItem}>
                        <div className={styles.timelineIcon}>{activityIcon[entry.activity_type] ?? '📝'}</div>
                        <div className={styles.timelineContent}>
                          <div className={styles.timelineTitle}>{entry.title}</div>
                          {entry.description && <div className={styles.timelineDesc}>{entry.description}</div>}
                        </div>
                        <div className={styles.timelineDate}>{formatDateFull(new Date(entry.created_at))}</div>
                      </div>
                    )) : <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13, textAlign: 'center' }}>対応履歴はまだありません</div>}
                  </div>
                </div>
              )}
              {activeTab === 'proposals' && (
                <div className={styles.proposalsTab}>
                  {/* 提案済み物件 */}
                  {proposals.length > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div className={styles.proposalsTitle}>提案済み物件（{proposals.length}件）</div>
                      </div>
                      {/* 選択アクションバー (提案済み物件がある限り常時表示) */}
                      <div className={styles.selectionBar}>
                        <span className={styles.selectionCount}>
                          {selectedProposalIds.size > 0 ? `${selectedProposalIds.size}件選択中` : '物件をチェックして一括紹介'}
                        </span>
                        <div className={styles.selectionActions}>
                          <button type="button" className={styles.selectionBtn} onClick={selectAllProposals}>全選択</button>
                          <button type="button" className={styles.selectionBtn} onClick={clearProposalSelection} disabled={selectedProposalIds.size === 0}>選択解除</button>
                          <button
                            type="button"
                            className={styles.selectionPrimaryBtn}
                            onClick={() => setShowBundlePreview(true)}
                            disabled={selectedProposalIds.size === 0}
                            title="選択した物件の紹介書をまとめて表示"
                          >📄 選択した物件をまとめて紹介</button>
                        </div>
                      </div>
                      <div className={styles.proposalsList}>
                        {proposals.map(pr => {
                          const checked = selectedProposalIds.has(pr.property_id);
                          return (
                          <div key={pr.id} className={`${styles.proposalCard} ${checked ? styles.proposalCardSelected : ''}`}>
                            <label className={styles.proposalCheckboxLabel} title="紹介書の一括出力に選択">
                              <input
                                type="checkbox"
                                className={styles.proposalCheckbox}
                                checked={checked}
                                onChange={() => toggleProposalSelection(pr.property_id)}
                              />
                            </label>
                            <div className={styles.proposalIcon}>📋</div>
                            <div className={styles.proposalInfo}>
                              <div className={styles.proposalName}>{pr.property_name ?? '物件'}</div>
                              <div className={styles.proposalMeta}>{pr.address ?? ''} / {pr.layout ?? ''}{pr.rent ? ` / ¥${pr.rent.toLocaleString()}/月` : ''}</div>
                              <div style={{ marginTop: 4 }}>
                                <Badge variant={proposalStatusVariant[pr.status] ?? 'gray'}>{proposalStatusLabel[pr.status] ?? pr.status}</Badge>
                                <span style={{ fontSize: 10, color: 'var(--ink3)', marginLeft: 8 }}>{new Date(pr.created_at).toLocaleDateString('ja-JP')}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              className={styles.proposalPreviewBtn}
                              onClick={() => setPreviewTarget({ id: pr.property_id, name: pr.property_name ?? '物件' })}
                              title="物件紹介書を表示"
                            >📄 物件紹介</button>
                            <select className={styles.proposalStatusSelect} value={pr.status} onChange={(e) => handleProposalStatus(pr.id, e.target.value)}>
                              <option value="proposed">提案中</option>
                              <option value="interested">興味あり</option>
                              <option value="viewing">内見予定</option>
                              <option value="rejected">見送り</option>
                            </select>
                          </div>
                          );
                        })}
                      </div>
                    </>
                  )}

                  {/* 条件に合う物件 */}
                  <div className={styles.proposalsTitle} style={proposals.length > 0 ? { marginTop: 20 } : undefined}>条件に合う物件</div>
                  <div className={styles.proposalsList}>
                    {getSuggestedProperties().map(p => {
                      const alreadyProposed = proposals.some(pr => pr.property_id === p.id);
                      return (
                        <div key={p.id} className={styles.proposalCard}>
                          <div className={styles.proposalIcon}>🏢</div>
                          <div className={styles.proposalInfo}>
                            <div className={styles.proposalName}>{p.name}</div>
                            <div className={styles.proposalMeta}>{p.address} / {p.layout} / {p.area}m²</div>
                            <div className={styles.proposalRent}>¥{p.rent?.toLocaleString()}<span className={styles.rentUnit}>/月</span></div>
                          </div>
                          <button
                            type="button"
                            className={styles.proposalPreviewBtn}
                            onClick={() => setPreviewTarget({ id: p.id, name: p.name })}
                            title="物件紹介書を表示"
                          >📄 物件紹介</button>
                          {alreadyProposed
                            ? <span className={styles.proposedLabel}>提案済</span>
                            : <button className={styles.proposalBtn} onClick={() => handlePropose(p.id)}>提案する</button>
                          }
                        </div>
                      );
                    })}
                    {getSuggestedProperties().length === 0 && <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13, textAlign: 'center' }}>条件に合う物件がありません</div>}
                  </div>
                </div>
              )}
              {activeTab === 'viewings' && (
                <div className={styles.proposalsTab}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div className={styles.proposalsTitle}>内見履歴（{viewings.length}件）</div>
                    <button className={styles.proposalPrintBtn} onClick={() => setShowViewingForm(true)}>＋ 内見予約</button>
                  </div>
                  {viewings.length === 0 ? (
                    <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13, textAlign: 'center' }}>内見予定/履歴はまだありません</div>
                  ) : (
                    <div className={styles.proposalsList}>
                      {viewings.map(v => {
                        const d = new Date(v.scheduled_at);
                        return (
                          <div key={v.id} className={styles.proposalCard}>
                            <div className={styles.proposalIcon}>🏠</div>
                            <div className={styles.proposalInfo}>
                              <div className={styles.proposalName}>
                                {d.toLocaleString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', weekday: 'short' })}
                                {v.property_name ? ` / ${v.property_name}` : ''}
                              </div>
                              <div className={styles.proposalMeta}>
                                {v.property_address || '—'}{v.meeting_place ? ` / 集合: ${v.meeting_place}` : ''}
                              </div>
                              <div style={{ marginTop: 4, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Badge variant={viewingStatusVariant[v.status] || 'gray'}>{viewingStatusLabel[v.status] || v.status}</Badge>
                                {v.result && <Badge variant="blue">{viewingResultLabel[v.result] || v.result}</Badge>}
                                {v.reminded_at && <span style={{ fontSize: 10, color: 'var(--ink3)' }}>📧 送信済</span>}
                                {v.note && <span style={{ fontSize: 11, color: 'var(--ink2)' }}>{v.note}</span>}
                              </div>
                            </div>
                            {v.status === 'scheduled' && (
                              <>
                                <button
                                  type="button"
                                  className={styles.proposalPreviewBtn}
                                  onClick={async () => {
                                    try {
                                      await api(`/tenant/customer-viewings/${v.id}/remind`, { method: 'POST' });
                                      toast('リマインダー送信しました', 'success');
                                      if (selected) api<Viewing[]>(`/tenant/customers/${selected.id}/viewings`).then(setViewings);
                                    } catch (e) { toast((e as Error)?.message || '送信失敗', 'error'); }
                                  }}
                                  title="リマインダーメール送信"
                                >📧 リマインド</button>
                                <button
                                  type="button"
                                  className={styles.proposalPreviewBtn}
                                  onClick={() => setResultTargetId(v.id)}
                                  title="内見結果登録 (完了扱いに)"
                                >✅ 結果登録</button>
                                <button
                                  type="button"
                                  className={styles.proposalPreviewBtn}
                                  onClick={() => {
                                    const d = new Date(v.scheduled_at);
                                    const pad = (n: number) => String(n).padStart(2, '0');
                                    setViewingForm({
                                      propertyId: v.property_id || '',
                                      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
                                      time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
                                      meetingPlace: v.meeting_place || '',
                                      note: v.note || '',
                                      assigneeId: v.assignee_id || '',
                                    });
                                    setEditingViewingId(v.id);
                                    setShowViewingForm(true);
                                  }}
                                  title="予約を編集"
                                >✏️ 編集</button>
                                <button
                                  type="button"
                                  className={styles.proposalBtn}
                                  style={{ background: '#64748b' }}
                                  onClick={async () => {
                                    if (!confirm('この内見予定をキャンセルしますか?')) return;
                                    try {
                                      await api(`/tenant/customer-viewings/${v.id}`, { method: 'DELETE' });
                                      toast('キャンセルしました', 'success');
                                      if (selected) api<Viewing[]>(`/tenant/customers/${selected.id}/viewings`).then(setViewings);
                                    } catch { toast('キャンセルに失敗', 'error'); }
                                  }}
                                >キャンセル</button>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'deals' && (
                <div className={styles.proposalsTab}>
                  <div className={styles.proposalsTitle}>取引一覧（{customerDeals.length}件）</div>
                  {customerDeals.length === 0 ? (
                    <div style={{ padding: 20, color: 'var(--ink4)', fontSize: 13, textAlign: 'center' }}>
                      この顧客に紐づく取引はありません
                    </div>
                  ) : (
                    <div className={styles.proposalsList}>
                      {customerDeals.map(d => (
                        <div key={d.id} className={styles.proposalCard}>
                          <div className={styles.proposalIcon}>{dealTypeIcon[d.deal_type] || '📋'}</div>
                          <div className={styles.proposalInfo}>
                            <div className={styles.proposalName}>{d.title}</div>
                            <div className={styles.proposalMeta}>
                              {dealTypeLabel[d.deal_type] || d.deal_type}
                              {d.property_name && ` / ${d.property_name}`}
                            </div>
                            <div style={{ marginTop: 4 }}>
                              <Badge variant={dealStatusVariant[d.status] ?? 'gray'}>
                                {dealStatusLabel[d.status] ?? d.status}
                              </Badge>
                              <span style={{ fontSize: 10, color: 'var(--ink3)', marginLeft: 8 }}>
                                作成: {new Date(d.created_at).toLocaleDateString('ja-JP')}
                              </span>
                              {d.closed_at && (
                                <span style={{ fontSize: 10, color: 'var(--ink3)', marginLeft: 8 }}>
                                  / 完了: {new Date(d.closed_at).toLocaleDateString('ja-JP')}
                                </span>
                              )}
                            </div>
                          </div>
                          <button className={styles.proposalBtn} onClick={() => navigate(`/contracts?tab=deals&deal=${d.id}`)}>詳細</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Customer Create Modal - モック準拠 */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="👤 顧客登録" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.actionBtnOutline} onClick={() => setShowCreate(false)}>キャンセル</button>
          <button className={styles.actionBtn} onClick={handleCreateCustomer}>顧客を登録する</button>
        </div>
      }>
        <div className={styles.createForm}>
          {/* 基本情報 */}
          <div className={styles.sectionTitle}>基本情報</div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>姓 *</label><input className={styles.formInput} value={createForm.lastName} onChange={e => setCreateForm({ ...createForm, lastName: e.target.value })} placeholder="金城" /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>名</label><input className={styles.formInput} value={createForm.firstName} onChange={e => setCreateForm({ ...createForm, firstName: e.target.value })} placeholder="一郎" /></div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>電話番号</label><input className={styles.formInput} value={createForm.phone} onChange={e => setCreateForm({ ...createForm, phone: e.target.value })} placeholder="0366669999" /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>メールアドレス</label><input className={styles.formInput} value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })} placeholder="test@gmail.com" /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>LINE ID（任意）</label><input className={styles.formInput} value={createForm.line_id} onChange={e => setCreateForm({ ...createForm, line_id: e.target.value })} placeholder="line_id" /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>顧客種別</label>
            <select className={styles.formInput} value={createForm.customer_type} onChange={e => setCreateForm({ ...createForm, customer_type: e.target.value })}>
              {customerTypes.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
            </select>
          </div>

          {/* 希望条件 */}
          <div className={styles.sectionTitle}>希望条件（借主・買主の場合）</div>
          <div className={styles.formGroup}><label className={styles.formLabel}>希望エリア</label><input className={styles.formInput} value={createForm.area} onChange={e => setCreateForm({ ...createForm, area: e.target.value })} placeholder="東京都" /></div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>希望間取り</label>
              <select className={styles.formInput} value={createForm.layout} onChange={e => setCreateForm({ ...createForm, layout: e.target.value })}>
                {layoutOptions.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>賃料・購入上限（円）</label><input className={styles.formInput} type="number" value={createForm.max_rent} onChange={e => setCreateForm({ ...createForm, max_rent: e.target.value })} placeholder="200,000" /></div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>入居・購入希望時期</label>
              <select className={styles.formInput} value={createForm.move_in} onChange={e => setCreateForm({ ...createForm, move_in: e.target.value })}>
                {moveInOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>ペット</label>
              <select className={styles.formInput} value={createForm.pet} onChange={e => setCreateForm({ ...createForm, pet: e.target.value })}>
                {petOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>その他要望・メモ</label>
            <textarea className={styles.formTextarea} value={createForm.notes} onChange={e => setCreateForm({ ...createForm, notes: e.target.value })} placeholder="例：駅徒歩10分以内、バルコニー広め、職場が〇〇…" rows={3} />
          </div>

          {/* 関心物件 */}
          <div className={styles.formGroup}><label className={styles.formLabel}>関心物件（物件選択）</label>
            <select className={styles.formInput} value={createForm.interested_property_id} onChange={e => setCreateForm({ ...createForm, interested_property_id: e.target.value })}>
              <option value="">未選択</option>
              {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name} -- {p.address}</option>)}
            </select>
          </div>

          {/* 流入・担当 */}
          <div className={styles.sectionTitle}>流入・担当</div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>問い合わせ元</label>
              <select className={styles.formInput} value={createForm.source} onChange={e => setCreateForm({ ...createForm, source: e.target.value })}>
                <option value="suumo">SUUMO</option><option value="homes">HOME'S</option><option value="athome">athome</option>
                <option value="website">自社サイト</option><option value="walk_in">来店</option><option value="referral">紹介</option>
                <option value="line">LINE</option><option value="other">その他</option>
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>担当者</label>
              <select className={styles.formInput} value={createForm.assignee} onChange={e => setCreateForm({ ...createForm, assignee: e.target.value })}>
                <option value="">選択してください</option>
                {(users ?? []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={createForm.privacy_consent} onChange={e => setCreateForm({ ...createForm, privacy_consent: e.target.checked })} /><span>個人情報取り扱いに同意する</span></label>
        </div>
      </Modal>

      {/* Customer Edit Modal */}
      <Modal open={showEdit} onClose={() => setShowEdit(false)} title="顧客情報編集" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.actionBtnOutline} onClick={() => setShowEdit(false)}>キャンセル</button>
          <button className={styles.actionBtn} onClick={handleEditCustomer}>保存</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>顧客名</label><input className={styles.formInput} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} /></div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>電話番号</label><input className={styles.formInput} value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>メール</label><input className={styles.formInput} value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} /></div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>ステータス</label><select className={styles.formInput} value={editForm.status} onChange={e => setEditForm({ ...editForm, status: e.target.value })}>
              {Object.entries(statusLabel).filter(([k]) => k !== 'contacted').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>担当者</label>
              <select className={styles.formInput} value={editForm.assignee_id} onChange={e => {
                const u = (users ?? []).find(x => x.id === e.target.value);
                setEditForm({ ...editForm, assignee_id: e.target.value, assignee: u?.name || '' });
              }}>
                <option value="">未割当</option>
                {(users ?? []).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>希望エリア</label><input className={styles.formInput} value={editForm.pref_area} onChange={e => setEditForm({ ...editForm, pref_area: e.target.value })} placeholder="渋谷区、目黒区" /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>希望間取り</label>
              <select className={styles.formInput} value={editForm.pref_layout} onChange={e => setEditForm({ ...editForm, pref_layout: e.target.value })}>
                <option value="">未設定</option>
                {layoutOptions.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>賃料上限</label><input className={styles.formInput} type="number" value={editForm.pref_max_rent} onChange={e => setEditForm({ ...editForm, pref_max_rent: e.target.value })} placeholder="150,000" /></div>
            <div className={styles.formGroup}><label className={styles.formLabel}>入居希望時期</label>
              <select className={styles.formInput} value={editForm.pref_move_in} onChange={e => setEditForm({ ...editForm, pref_move_in: e.target.value })}>
                <option value="">未設定</option>
                {moveInOptions.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}><label className={styles.formLabel}>ペット</label>
              <select className={styles.formInput} value={editForm.pref_pet} onChange={e => setEditForm({ ...editForm, pref_pet: e.target.value })}>
                <option value="">未設定</option>
                {petOptions.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className={styles.formGroup}><label className={styles.formLabel}>顧客種別</label>
              <select className={styles.formInput} value={editForm.pref_customer_type} onChange={e => setEditForm({ ...editForm, pref_customer_type: e.target.value })}>
                <option value="">未設定</option>
                {customerTypes.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>問い合わせ元</label><select className={styles.formInput} value={editForm.source} onChange={e => setEditForm({ ...editForm, source: e.target.value })}>
            <option value="">未設定</option><option value="website">自社サイト</option><option value="suumo">SUUMO</option><option value="homes">HOMES</option><option value="athome">at home</option><option value="referral">紹介</option><option value="walk_in">来店</option><option value="phone">電話</option><option value="other">その他</option>
          </select></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>関心物件（テキスト）</label><input className={styles.formInput} value={editForm.property_interest} onChange={e => setEditForm({ ...editForm, property_interest: e.target.value })} /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>関心物件（物件選択）</label><select className={styles.formInput} value={editForm.interested_property_id} onChange={e => setEditForm({ ...editForm, interested_property_id: e.target.value })}>
            <option value="">未選択</option>
            {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name} — {p.address}</option>)}
          </select></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>備考</label><textarea className={styles.formTextarea} value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} rows={3} /></div>
        </div>
      </Modal>

      {/* Add History Modal */}
      <Modal open={showAddHistory} onClose={() => setShowAddHistory(false)} title="対応履歴追加" footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.actionBtnOutline} onClick={() => setShowAddHistory(false)}>キャンセル</button>
          <button className={styles.actionBtn} onClick={handleAddHistory}>追加</button>
        </div>
      }>
        <div className={styles.createForm}>
          <div className={styles.formGroup}><label className={styles.formLabel}>種別</label><select className={styles.formInput} value={historyForm.activity_type} onChange={e => setHistoryForm({ ...historyForm, activity_type: e.target.value })}>
            <option value="inquiry">問い合わせ</option><option value="call">電話</option><option value="email">メール</option><option value="line">LINE</option><option value="viewing">内見</option><option value="application">申込</option><option value="contract">契約</option><option value="note">メモ</option>
          </select></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>タイトル *</label><input className={styles.formInput} value={historyForm.title} onChange={e => setHistoryForm({ ...historyForm, title: e.target.value })} placeholder="初回電話対応" /></div>
          <div className={styles.formGroup}><label className={styles.formLabel}>詳細</label><textarea className={styles.formTextarea} value={historyForm.description} onChange={e => setHistoryForm({ ...historyForm, description: e.target.value })} placeholder="対応内容を記録..." rows={4} /></div>
        </div>
      </Modal>

      {/* 物件紹介プレビュー (単体) */}
      <PropertyPreviewModal
        open={!!previewTarget}
        onClose={() => setPreviewTarget(null)}
        propertyId={previewTarget?.id ?? null}
        propertyName={previewTarget?.name}
      />

      {/* 物件紹介プレビュー (複数・提案物件一括) */}
      <PropertyPreviewModal
        open={showBundlePreview}
        onClose={() => setShowBundlePreview(false)}
        propertyIds={Array.from(selectedProposalIds)}
        bundleTitle={selected ? `${selectedProposalIds.size}件 / ${selected.name} 様向け` : `${selectedProposalIds.size}件`}
        bundleDownloadName={selected ? `${selected.name}様_物件紹介書_${selectedProposalIds.size}件` : `物件紹介書_${selectedProposalIds.size}件`}
      />

      {/* 内見予約モーダル (新規/編集両対応) */}
      <Modal open={showViewingForm} onClose={() => { setShowViewingForm(false); setEditingViewingId(null); }} title={editingViewingId ? '内見予約の編集' : '内見予約'} footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className={styles.cancelBtn} onClick={() => { setShowViewingForm(false); setEditingViewingId(null); }}>キャンセル</button>
          <button className={styles.saveBtn} onClick={async () => {
            if (!selected) return;
            if (!viewingForm.date || !viewingForm.time) { toast('日付と時刻を指定してください', 'error'); return; }
            const scheduledAt = new Date(`${viewingForm.date}T${viewingForm.time}:00`).toISOString();
            try {
              if (editingViewingId) {
                await api(`/tenant/customer-viewings/${editingViewingId}`, {
                  method: 'PATCH',
                  body: JSON.stringify({
                    property_id: viewingForm.propertyId || undefined,
                    scheduled_at: scheduledAt,
                    assignee_id: viewingForm.assigneeId || undefined,
                    meeting_place: viewingForm.meetingPlace || undefined,
                    note: viewingForm.note || undefined,
                  }),
                });
                toast('内見予約を更新しました', 'success');
              } else {
                await api('/tenant/customer-viewings', {
                  method: 'POST',
                  body: JSON.stringify({
                    customer_id: selected.id,
                    property_id: viewingForm.propertyId || undefined,
                    scheduled_at: scheduledAt,
                    assignee_id: viewingForm.assigneeId || undefined,
                    meeting_place: viewingForm.meetingPlace || undefined,
                    note: viewingForm.note || undefined,
                  }),
                });
                toast('内見予約を登録しました', 'success');
              }
              setShowViewingForm(false);
              setEditingViewingId(null);
              setViewingForm({ propertyId: '', date: '', time: '', meetingPlace: '', note: '', assigneeId: '' });
              api<Viewing[]>(`/tenant/customers/${selected.id}/viewings`).then(setViewings);
            } catch (e) { toast((e as Error)?.message || '保存失敗', 'error'); }
          }}>{editingViewingId ? '更新' : '予約登録'}</button>
        </div>
      }>
        <div className={styles.formStack}>
          <div className={styles.formGroup}><label className={styles.formLabel}>物件 (任意)</label>
            <select className={styles.formInput} value={viewingForm.propertyId} onChange={e => setViewingForm({ ...viewingForm, propertyId: e.target.value })}>
              <option value="">選択しない</option>
              {(properties ?? []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>日付 *</label>
              <input type="date" className={styles.formInput} value={viewingForm.date} onChange={e => setViewingForm({ ...viewingForm, date: e.target.value })} />
            </div>
            <div className={styles.formGroup} style={{ flex: 1 }}><label className={styles.formLabel}>時間 *</label>
              <input type="time" className={styles.formInput} value={viewingForm.time} onChange={e => setViewingForm({ ...viewingForm, time: e.target.value })} />
            </div>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>担当者 (任意 / 未指定は自分)</label>
            <select className={styles.formInput} value={viewingForm.assigneeId} onChange={e => setViewingForm({ ...viewingForm, assigneeId: e.target.value })}>
              <option value="">自分 ({authUser?.name || '未設定'})</option>
              {(users ?? []).filter(u => u.id !== authUser?.id).map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>集合場所</label>
            <input className={styles.formInput} value={viewingForm.meetingPlace} onChange={e => setViewingForm({ ...viewingForm, meetingPlace: e.target.value })} placeholder="現地 / 渋谷駅ハチ公前 など" />
          </div>
          <div className={styles.formGroup}><label className={styles.formLabel}>メモ</label>
            <textarea className={styles.formTextarea} value={viewingForm.note} onChange={e => setViewingForm({ ...viewingForm, note: e.target.value })} rows={2} />
          </div>
        </div>
      </Modal>

      {/* 内見結果登録モーダル (共通コンポーネント) */}
      <ViewingResultModal
        open={!!resultTargetId}
        onClose={() => setResultTargetId(null)}
        viewingId={resultTargetId}
        subTitle={selected?.name}
        onSaved={() => {
          if (selected) api<Viewing[]>(`/tenant/customers/${selected.id}/viewings`).then(setViewings);
        }}
      />

      {/* 申込詳細モーダル */}
      <ApplicationSubmissionModal
        open={!!submissionModalId}
        submissionId={submissionModalId}
        onClose={() => setSubmissionModalId(null)}
        onStatusChanged={() => {
          if (selected) api<ApplicationForm[]>(`/tenant/customers/${selected.id}/application-forms`).then(setApplicationForms).catch(() => {});
        }}
      />
    </div>
  );
}

function formatDateFull(date: Date): string {
  return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}
